import React, { useState, useMemo, useEffect } from "react";
import dayjs from "dayjs";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Paper,
    Box,
    Checkbox,
    FormControl,
    MenuItem,
    Select,
    ListItemText,
    OutlinedInput,
    InputLabel,
    CircularProgress,
    Typography,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DatePicker } from "@mui/x-date-pickers";
import { DataGrid } from "@mui/x-data-grid";
import { skipToken } from "@reduxjs/toolkit/query/react";

import { tokens } from "../../../../theme";
import { useGetTopicSummaryQuery } from "../../../../features/market_pulse/marketPulseApiSlice";

/* -------------------------------------------------- */
/*  constants & helpers                               */
/* -------------------------------------------------- */
const SEARCH_OPTS = [
    { value: "TOPIC_ID", label: "Topic ID" },
    { value: "TOPIC", label: "Topic Name" },
    { value: "DESCRIPTION", label: "Description" },
];

const makeSearchFilter = (fields, term) =>
    term
        ? (row) =>
            fields.some((f) =>
                String(row[f] ?? "").toLowerCase().includes(term.toLowerCase())
            )
        : () => true;

/* -------------------------------------------------- */
/*  component                                         */
/* -------------------------------------------------- */
const DailyTopicsList = ({ rows = [], loading, date, onDateChange }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    /* products --------------------------------------- */
    const products = useMemo(() => [...new Set(rows.map(r => r.PRODUCT))].sort(), [rows]);
    const [selectedProducts, setSelectedProducts] = useState([]);

    /* search ----------------------------------------- */
    const [searchTerm, setSearchTerm] = useState("");
    const [searchFields, setSearchFields] = useState(SEARCH_OPTS.map(o => o.value));

    const filteredRows = useMemo(() => {
        const prodTest = selectedProducts.length
            ? r => selectedProducts.includes(r.PRODUCT)
            : () => true;
        const searchTest = makeSearchFilter(searchFields, searchTerm);
        return rows.filter(r => prodTest(r) && searchTest(r));
    }, [rows, selectedProducts, searchFields, searchTerm]);

    /* popup ------------------------------------------ */
    const [selectedTopic, setSelectedTopic] = useState(null);
    const { data: topicSummary } = useGetTopicSummaryQuery(
        selectedTopic
            ? { topicId: selectedTopic.TOPIC_ID, date: dayjs(date).format("YYYY-MM-DD") }
            : skipToken
    );

    /* grid columns ----------------------------------- */
    const columns = [
        { field: "PRODUCT", headerName: "Product", flex: 1 },
        { field: "TOPIC_ID", headerName: "Topic ID", flex: 1.3 },
        { field: "TOPIC", headerName: "Topic", flex: 1.5 },
        { field: "DESCRIPTION", headerName: "Description", flex: 3 },
        {
            field: "RECORD_COUNT",
            headerName: "Count",
            type: "number",
            flex: 0.8,
            valueFormatter: ({ value }) => Number(value).toLocaleString(),
        },
    ];

    /* ui --------------------------------------------- */
    return (
        <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h4" fontWeight="bold">Daily Topic List</Typography>
            </AccordionSummary>

            <AccordionDetails>

                {/* unified toolbar */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        mb: 3,
                        flexWrap: "wrap",
                        alignItems: "center",
                    }}
                >
                    <DatePicker
                        label="Select Day"
                        value={date}
                        onChange={onDateChange}
                    />
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            alignItems: "center",
                            borderRadius: 2,
                            /* primary-400 → primary-800 */
                            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[800]} 100%)`,
                        }}
                    >
                        {/* title */}
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, width: "100%" }}
                        >
                            Topic Filters
                        </Typography>

                        {/* product select ------------------------------------------------ */}
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <Select
                                multiple
                                displayEmpty
                                value={selectedProducts}
                                onChange={(e) => setSelectedProducts(e.target.value)}
                                renderValue={(sel) => (sel.length ? sel.join(", ") : "All Products")}
                            >
                                {products.map((prod) => (
                                    <MenuItem key={prod} value={prod}>
                                        <Checkbox checked={selectedProducts.includes(prod)} />
                                        <ListItemText primary={prod} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* search term --------------------------------------------------- */}
                        <TextField
                            label="Search…"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />

                        {/* search-field selector ---------------------------------------- */}
                        <FormControl size="small">
                            <InputLabel id="search-field-label">Search By</InputLabel>
                            <Select
                                labelId="search-field-label"
                                multiple
                                value={searchFields}
                                onChange={(e) => setSearchFields(e.target.value)}
                                input={
                                    <OutlinedInput
                                        label="Search By"
                                        sx={{ "& .MuiOutlinedInput-notchedOutline": { borderWidth: 2 } }}
                                    />
                                }
                                renderValue={(sel) =>
                                    sel
                                        .map((v) => SEARCH_OPTS.find((o) => o.value === v)?.label)
                                        .join(", ")
                                }
                            >
                                {SEARCH_OPTS.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>
                                        <Checkbox checked={searchFields.includes(value)} />
                                        <ListItemText primary={label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Paper>

                </Box>

                {/* grid / spinner */}
                {loading ? (
                    <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                        <CircularProgress size={56} sx={{ color: colors.greenAccent[400] }} />
                    </Box>
                ) : (
                    <DataGrid
                        autoHeight
                        rows={filteredRows}
                        columns={columns}
                        getRowId={(r) => `${r.PRODUCT}-${r.TOPIC_ID}`}
                        pageSize={25}
                        disableRowSelectionOnClick
                        sx={{
                            "& .MuiDataGrid-columnHeaders": {
                                bgcolor: colors.primary[500],
                                "& .MuiDataGrid-columnHeaderTitle": {
                                    fontSize: "0.95rem",
                                    fontWeight: 600,
                                },
                            },
                        }}
                        onRowClick={({ row }) => setSelectedTopic(row)}
                    />
                )}

                {/* popup */}
                <Dialog
                    open={Boolean(selectedTopic)}
                    onClose={() => setSelectedTopic(null)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>{selectedTopic?.TOPIC}</DialogTitle>
                    <DialogContent dividers>
                        <Typography sx={{ mb: 1 }}>
                            <strong>Topic ID:</strong> {selectedTopic?.TOPIC_ID}
                        </Typography>
                        <Typography sx={{ mb: 1 }}>
                            <strong>Product:</strong> {selectedTopic?.PRODUCT}
                        </Typography>
                        <Typography sx={{ mb: 1 }}>
                            <strong>Day Count:</strong>{" "}
                            {Number(selectedTopic?.RECORD_COUNT ?? 0).toLocaleString()}
                        </Typography>
                        <Typography sx={{ mb: 1 }}>
                            <strong>Description:</strong> {selectedTopic?.DESCRIPTION}
                        </Typography>

                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1">Look-back (D/W/M)</Typography>
                            <Typography color="text.secondary">
                                {topicSummary
                                    ? `${topicSummary.DAY_COUNT.toLocaleString()} / ${topicSummary.WEEK_COUNT.toLocaleString()} / ${topicSummary.MONTH_COUNT.toLocaleString()}`
                                    : "Loading…"}
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setSelectedTopic(null)}>Close</Button>
                    </DialogActions>
                </Dialog>
            </AccordionDetails>
        </Accordion>
    );
};

export default DailyTopicsList;
